import { ChevronRightSharp } from '@mui/icons-material';
import { AccordionDetails, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { StyledAccordion, StyledAccordionSummary } from '../../theme/CustomComponents';
import type { CategoryProgress, ProjectWithLastScanned } from '../../Types/dealGPT';
import { ChatMode, DealGPTDiscoveryView, DealGPTQuestionStatus } from '../../Types/enums';
import CategoryAccordion from './CategoryAccordion';
import ChatIconButton from './ChatIconButton';
import StatusProgressBar from './StatusProgressBar';

export type ScopeAccordionProps = {
  runId: string;
  project: ProjectWithLastScanned;
  progress: CategoryProgress[];
  expandAll: boolean;
  discoveryView: DealGPTDiscoveryView;
  selectedStatusFilters: DealGPTQuestionStatus[];
  searchQuery: string;
  isIndexing: boolean;
  setShouldFetchRunProgress: (isRescan: boolean) => void;
};

export const ScopeAccordion = ({
  progress,
  expandAll,
  ...categoryProps
}: ScopeAccordionProps): JSX.Element => {
  const [expandScope, setExpandScope] = useState<boolean>(expandAll);
  const scopeProgress = useMemo(
    () => ({
      quantity: progress.reduce((acc, category) => acc + category.quantity, 0),
      quantityAnswered: progress.reduce((acc, category) => acc + category.quantityAnswered, 0),
      quantityMoreInfoNeeded: progress.reduce(
        (acc, category) => acc + category.quantityMoreInfoNeeded,
        0
      ),
      quantityNotFound: progress.reduce((acc, category) => acc + category.quantityNotFound, 0),
      quantitySkipped: progress.reduce((acc, category) => acc + category.quantitySkipped, 0),
    }),
    [progress]
  );

  const totalQuestions = useMemo(
    () => progress.reduce((acc, category) => acc + category.quantity, 0),
    [progress]
  );

  useEffect(() => {
    setExpandScope(expandAll);
  }, [expandAll]);

  const handleAccordionChange = useCallback(() => {
    setExpandScope((prev) => !prev);
  }, []);

  const [category] = progress;

  return (
    <StyledAccordion square disableGutters expanded={expandScope} onChange={handleAccordionChange}>
      <StyledAccordionSummary expandIcon={<ChevronRightSharp />}>
        <div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Typography variant="subtitle2" sx={{ marginTop: '8px', marginRight: 1 }}>
              {category.scope}
            </Typography>
            <ChatIconButton
              project={categoryProps.project}
              mode={ChatMode.DISCOVERY}
              currentCollectionId={category.collectionId}
            />
          </div>
          <Typography sx={{ fontStyle: 'italic' }}>{totalQuestions} questions</Typography>
        </div>
        <div style={{ width: '33%', height: '20px', marginTop: '8px' }}>
          <StatusProgressBar
            quantity={scopeProgress.quantity}
            quantityAnswered={scopeProgress.quantityAnswered}
            quantityMoreInfoNeeded={scopeProgress.quantityMoreInfoNeeded}
            quantityNotFound={scopeProgress.quantityNotFound}
            quantitySkipped={scopeProgress.quantitySkipped}
          />
        </div>
      </StyledAccordionSummary>
      <AccordionDetails>
        {progress.map((category) => (
          <CategoryAccordion
            key={category.name}
            category={category}
            expandAll={expandAll}
            {...categoryProps}
          />
        ))}
      </AccordionDetails>
    </StyledAccordion>
  );
};

export default ScopeAccordion;
